import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { FixedAssetCountService } from '../../services/fixedAssetCount.service';
import { FixedAssetTransferService } from '../../services/fixedAssetTransfer.service';
import { CostCentreService } from '../../services/costCentre.service';
import { AssetLocation } from '../../interfaces/fixedAsset/fixedAssetTransfer.interface';
import { DepartmentOption } from '../../interfaces/costCentre.interface';
import { AssetCount, COUNT_RESULTS, COUNT_RESULT_LABELS } from '../../interfaces/fixedAsset/fixedAssetCount.interface';
import { formatDate } from '../../utils/dateUtils';
import { humanizeEnum } from '../../utils/format';

const STATUS_COLOR = { OPEN: 'info', CLOSED: 'success', CANCELLED: 'default' } as const;

/** Asset counts: the list of physical verification rounds, and the dialog that starts a new one. */
const FixedAssetCounts: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canVerify = usePermission(PERMISSIONS.FIXED_ASSET_VERIFY);
  const [rows, setRows] = useState<AssetCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [startOpen, setStartOpen] = useState(false);
  const [name, setName] = useState('');
  const [locations, setLocations] = useState<AssetLocation[]>([]);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [pickedLocations, setPickedLocations] = useState<AssetLocation[]>([]);
  const [pickedDepartments, setPickedDepartments] = useState<DepartmentOption[]>([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await FixedAssetCountService.list();
      setRows(Array.isArray(res) ? res : []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load asset counts');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openStart = () => {
    setName(''); setPickedLocations([]); setPickedDepartments([]);
    setStartOpen(true);
    FixedAssetTransferService.locations().then(r => setLocations(Array.isArray(r) ? r : []))
      .catch(e => { setLocations([]); toast.showApiError(e, 'Failed to load asset locations'); });
    CostCentreService.getDepartments().then(r => setDepartments(Array.isArray(r) ? r : []))
      .catch(e => { setDepartments([]); toast.showApiError(e, 'Failed to load departments'); });
  };

  const start = async () => {
    setBusy(true);
    try {
      const created = await FixedAssetCountService.start({
        name: name.trim(),
        locationIds: pickedLocations.map(l => l.id),
        departmentIds: pickedDepartments.map(d => d.id),
      });
      toast.showSuccess('Count started');
      setStartOpen(false);
      navigate(`/fixed-assets/counts/${created.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to start the count');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="fixedAssets.counts.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography variant="h5">Asset counts</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button data-help="fixedAssets.cnt.action.back" variant="outlined" onClick={() => navigate('/fixed-assets')}>Back to assets</Button>
          {canVerify && (
            <Button data-help="fixedAssets.counts.start" variant="contained" onClick={openStart}>Start a count</Button>
          )}
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A count checks the register against what is physically there. Scan each asset's label; assets nobody scanned are marked not found when you close the count. Nothing moves in the register.
      </Typography>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table size="small" data-help-section="fixedAssets.cnt.table">
            <TableHead>
              <TableRow>
                <TableCell data-help="fixedAssets.cnt.col.ref">Ref</TableCell>
                <TableCell data-help="fixedAssets.cnt.col.name">Name</TableCell>
                <TableCell data-help="fixedAssets.cnt.col.status">Status</TableCell>
                <TableCell data-help="fixedAssets.cnt.col.scope">Scope</TableCell>
                <TableCell data-help="fixedAssets.cnt.col.started">Started</TableCell>
                <TableCell data-help="fixedAssets.cnt.col.assets" align="right">Assets</TableCell>
                <TableCell data-help="fixedAssets.cnt.col.found" align="right">Found</TableCell>
                <TableCell data-help="fixedAssets.cnt.col.pending" align="right">Pending</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/fixed-assets/counts/${r.id}`)}>
                  <TableCell>{r.refNo}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={STATUS_COLOR[r.status]} /></TableCell>
                  <TableCell>{[...r.locationNames, ...r.departmentNames].join(', ') || 'All assets'}</TableCell>
                  <TableCell>{r.startedAt ? formatDate(r.startedAt) : '—'}</TableCell>
                  <TableCell align="right">{r.totalLines}</TableCell>
                  <TableCell align="right">{r.resultCounts.FOUND ?? 0}</TableCell>
                  <TableCell align="right">{r.resultCounts.PENDING ?? 0}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">No counts yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog data-help-page="fixedAssets.countStart.page" open={startOpen} onClose={busy ? undefined : () => setStartOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Start an asset count</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField data-help="fixedAssets.countStart.field.name" label="Count name" size="small" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Factory floor, October" />
            <Autocomplete<AssetLocation, true> multiple size="small" options={locations} value={pickedLocations}
              onChange={(_, v) => setPickedLocations(v)} getOptionLabel={l => `${l.code} - ${l.name}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={p => <TextField data-help="fixedAssets.countStart.field.locations" {...p} label="Locations (leave empty for all)" />} />
            <Autocomplete<DepartmentOption, true> multiple size="small" options={departments} value={pickedDepartments}
              onChange={(_, v) => setPickedDepartments(v)} getOptionLabel={d => d.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={p => <TextField data-help="fixedAssets.countStart.field.departments" {...p} label="Departments (leave empty for all)" />} />
            <Typography variant="caption" color="text.secondary">
              The count lists every asset not yet disposed that matches the places you pick. Result types: {COUNT_RESULTS.map(r => COUNT_RESULT_LABELS[r]).join(', ')}.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button data-help="fixedAssets.countStart.cancel" onClick={() => setStartOpen(false)} disabled={busy}>Cancel</Button>
          <Button data-help="fixedAssets.cnt.action.startConfirm" variant="contained" onClick={start} disabled={busy || !name.trim()}>Start</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FixedAssetCounts;
